import React, { useState } from 'react';
import styled from 'styled-components';

import { founderTypes } from '../data/founderTypes';
import { formatFounderTitle } from '../utils/textUtils';
import { getResultDrivers } from '../utils/profileCalculator';
import ResultDriversList from './ResultDriversList';

const Panel = styled.div`
  margin-top: 1.25rem;
`;

const ToggleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const ToggleButton = styled.button`
  font-size: 0.82rem;
  font-weight: 700;
  padding: 0.45rem 0.85rem;
  border-radius: 999px;
  cursor: pointer;
  border: 1px solid ${(props) => (props.$active ? 'rgba(var(--primary-rgb), 0.45)' : 'rgba(255, 255, 255, 0.12)')};
  background: ${(props) => (props.$active ? 'rgba(var(--primary-rgb), 0.16)' : 'rgba(255, 255, 255, 0.04)')};
  color: ${(props) => (props.$active ? 'var(--text-primary)' : 'var(--text-secondary)')};

  &:focus-visible {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }
`;

const EmptyNote = styled.p`
  margin: 0;
  color: var(--text-secondary);
  line-height: 1.6;
`;

const HelperText = styled.p`
  margin: 0 0 0.85rem;
  font-size: 0.9rem;
  color: var(--text-secondary);
  line-height: 1.5;
`;

const DRIVER_VIEWS = {
  primary: 'primary',
  all: 'all',
};

const ResultDriversPanel = ({ profile, limit = 5 }) => {
  const [view, setView] = useState(DRIVER_VIEWS.primary);

  if (!profile?.primaryType) return null;

  const primary = founderTypes[profile.primaryType];
  const primaryDrivers = getResultDrivers(profile, { limit, typeFilter: profile.primaryType });
  const allDrivers = getResultDrivers(profile, { limit });
  const activeDrivers = view === DRIVER_VIEWS.primary ? primaryDrivers : allDrivers;

  if (primaryDrivers.length === 0 && allDrivers.length === 0) {
    return (
      <Panel>
        <EmptyNote>
          Retake the test to see which answers shaped your {formatFounderTitle(primary?.title || 'founder type')} result.
        </EmptyNote>
      </Panel>
    );
  }

  return (
    <Panel aria-label="Answers that shaped your result">
      <ToggleRow role="tablist" aria-label="Driver view">
        <ToggleButton
          type="button"
          role="tab"
          aria-selected={view === DRIVER_VIEWS.primary}
          $active={view === DRIVER_VIEWS.primary}
          onClick={() => setView(DRIVER_VIEWS.primary)}
        >
          Primary type ({primary?.code || profile.primaryType})
        </ToggleButton>
        <ToggleButton
          type="button"
          role="tab"
          aria-selected={view === DRIVER_VIEWS.all}
          $active={view === DRIVER_VIEWS.all}
          onClick={() => setView(DRIVER_VIEWS.all)}
        >
          All top answers
        </ToggleButton>
      </ToggleRow>

      <HelperText>
        {view === DRIVER_VIEWS.primary
          ? `Answers that most strongly pointed to ${formatFounderTitle(primary?.title || 'your primary type')}, including higher-weight questions.`
          : 'Your highest-weight answers across all six founder types, regardless of which type they support.'}
      </HelperText>

      {activeDrivers.length > 0 ? (
        <ResultDriversList
          drivers={activeDrivers}
          primaryType={profile.primaryType}
          highlightPrimary={view === DRIVER_VIEWS.all}
        />
      ) : (
        <EmptyNote>
          No primary-type answers in the top weighted set. Switch to “All top answers” to see what shaped the blend.
        </EmptyNote>
      )}
    </Panel>
  );
};

export default ResultDriversPanel;
